import Button from '../Button/Button';
import { Container, TextContainer, Title, ErrorImage } from './styles';
import { RefreshIcon, UploadIcon } from '@/icons';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getBackendErrorCode } from '@/helpers';
import { ROUTES } from '@/constants/constants';
import { logEvent } from '../../../helpers/eventLogger';

// Codes for which refreshing is pointless (the file is gone) — offer a fresh
// upload instead.
const FILE_GONE_CODES = ['FILE_IS_DELETED', 'NO_ATTACHMENT_FOUND'];

interface ApplicationErrorProps {
  error?: unknown;
}

const ApplicationError = ({ error }: ApplicationErrorProps) => {
  const { t, i18n } = useTranslation('web', { keyPrefix: 'components.global.applicationError' });
  const [isRefreshing, setIsRefreshing] = useState(false);

  // When the failure carries a known backend error code (e.g. FILE_IS_DELETED),
  // show its translated message instead of the generic title. Falls back to the
  // generic title for plain JS errors (e.g. the top-level ErrorBoundary).
  const backendCode = getBackendErrorCode(error);
  const title = backendCode && i18n.exists(`back:${backendCode}`)
    ? i18n.t(`back:${backendCode}`)
    : t('title');
  const isFileGone = FILE_GONE_CODES.includes(backendCode);

  useEffect(() => {
    logEvent({
      logType: 'ERROR',
      eventType: 'PROCESS_FILE_ERROR',
      trace: error instanceof Error ? error.stack || error.message : String(error)
    });
  }, [error]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    window.location.reload();
  };

  // Full navigation (not react-router) so this works even when ApplicationError
  // renders as the top-level ErrorBoundary fallback, outside <BrowserRouter>.
  const handleUploadNew = () => {
    window.location.assign(ROUTES.home(i18n.language));
  };

  return (
    <Container>
      <TextContainer>
        <Title>{title}</Title>
        {isFileGone ? (
          <Button
            onClick={handleUploadNew}
            prefixIcon={<UploadIcon color="inherit" />}
            style={{ paddingLeft: '16px' }}
          >
            {t('uploadNew')}
          </Button>
        ) : (
          <Button
            onClick={handleRefresh}
            prefixIcon={<RefreshIcon color="inherit" />}
            style={{ paddingLeft: '16px' }}
            disabled={isRefreshing}
          >
            {isRefreshing ? t('refreshing') : t('refresh')}
          </Button>
        )}
      </TextContainer>
      <ErrorImage />
    </Container>
  );
};

export default ApplicationError;
